import { createContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { generateEndpoint } from "utils/helpers/generateEndpoint";

const addCartItem = (cartItems, productToAdd) => {
  const isAddOn = productToAdd.type === "add-on" || productToAdd.metadata?.type === "addon";

  const existingCartItem = cartItems.find((cartItem) => cartItem.id === productToAdd.id);
  const existingAddOnItem = cartItems.find(
    (cartItem) => (cartItem.type === "add-on" || cartItem.metadata?.type === "addon") && cartItem.id === productToAdd.id
  );

  // Don't allow duplicate items (same ID)
  if (existingCartItem) {
    return null;
  }

  // Don't allow duplicate add-ons
  if (isAddOn && existingAddOnItem) {
    return null;
  }

  // Allow multiple packages to be added - validation happens in cart display
  return [...cartItems, { ...productToAdd, quantity: 1 }];
};

const removeCartItem = (cartItems, cartItemToRemove) => {
  const existingCartItem = cartItems.find((cartItem) => cartItem.id === cartItemToRemove.id);

  if (existingCartItem.quantity === 1) {
    return cartItems.filter((cartItem) => cartItem.id !== cartItemToRemove.id);
  }

  return cartItems.map((cartItem) => (cartItem.id === cartItemToRemove.id ? { ...cartItem, quantity: cartItem.quantity - 1 } : cartItem));
};

const removeMultipleCartItems = (cartItems, itemsToRemove) => {
  return itemsToRemove.reduce((updatedCartItems, itemToRemove) => {
    return removeCartItem(updatedCartItems, itemToRemove);
  }, cartItems);
};

const validatePromoCode = (code, coupons, cartItems) => {
  // Check if this is the EHR-BETA coupon (case insensitive)
  if (code.toLowerCase() === "ehr-beta") {
    // Check if cart has any EHR items
    const hasEHRItems = cartItems.some((item) => item.metadata?.type === "ehr-subscription");

    // Check if cart has any non-EHR items (packages or add-ons that aren't EHR)
    const hasNonEHRItems = cartItems.some(
      (item) =>
        item.metadata?.type !== "ehr-subscription" &&
        (item.metadata?.type === "package" || item.type === "package" || item.metadata?.type === "addon" || item.type === "add-on")
    );

    // If no EHR items in cart, coupon is invalid
    if (!hasEHRItems) {
      return null;
    }

    // If cart has both EHR and non-EHR items, coupon is invalid
    if (hasEHRItems && hasNonEHRItems) {
      return null;
    }

    // Cart has only EHR items, find and return the coupon
    const coupon = coupons?.find((coupon) => coupon.name.toLowerCase() === code.toLowerCase());
    return coupon;
  }

  // For all other coupons, use the original logic
  const coupon = coupons?.find((coupon) => coupon.name.toLowerCase() === code.toLowerCase());
  return coupon;
};

export const CartContext = createContext({
  cartItems: [],
  addItemToCart: () => {},
  removeItemFromCart: () => {},
  cartTotal: 0,
  cartCount: 0,
  existsInCart: false,
  setExistsInCart: () => {},
  promoCode: null,
  applyPromoCode: () => {},
  originalSubtotal: 0,
  couponNotValid: false,
  stripeCouponObj: {},
  setStripeCouponObj: () => {},
  isCartOpen: false,
  setIsCartOpen: () => {},
  setCouponNotValid: () => {},
  isCartEmpty: false,
  setIsCartEmpty: () => {},
  clearCart: () => {},
  removeMultipleItemsFromCart: () => {},
});

export const CartProvider = ({ children }) => {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  const [promoCode, setPromoCode] = useState(null);
  const [stripeCouponObj, setStripeCouponObj] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [originalSubtotal, setOriginalSubtotal] = useState(0);
  const [couponNotValid, setCouponNotValid] = useState(false);
  const [isCartEmpty, setIsCartEmpty] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const location = useLocation();

  // Fetch coupons on component mount
  useEffect(() => {
    const fetchCoupons = async () => {
      const endpoint = generateEndpoint(
        process.env.REACT_APP_AWS_DEV_GET_STRIPE_COUPONS_ENDPOINT,
        process.env.REACT_APP_AWS_PROD_GET_STRIPE_COUPONS_ENDPOINT
      );
      try {
        const response = await axios.get(endpoint);
        setCoupons(response.data.data);
      } catch (error) {
        console.error("Error fetching coupons:", error);
      }
    };

    fetchCoupons();
  }, []);

  // Load cart data from localStorage on initial load
  useEffect(() => {
    const storedCartItems = JSON.parse(localStorage.getItem("cartItems"));
    const storedPromoCode = JSON.parse(localStorage.getItem("promoCode"));
    const storedDiscount = JSON.parse(localStorage.getItem("discount"));
    const storedStripeCouponObj = JSON.parse(localStorage.getItem("stripeCouponObj"));

    if (storedCartItems) setCartItems(storedCartItems);
    if (storedPromoCode) setPromoCode(storedPromoCode);
    if (storedDiscount) setDiscount(storedDiscount);
    if (storedStripeCouponObj) setStripeCouponObj(storedStripeCouponObj);
  }, []);

  // Save cart data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
  }, [cartItems]);

  // Save promoCode and discount to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("promoCode", JSON.stringify(promoCode));
    localStorage.setItem("discount", JSON.stringify(discount));
    localStorage.setItem("stripeCouponObj", JSON.stringify(stripeCouponObj));
  }, [promoCode, discount, stripeCouponObj]);

  // Update cart count and original subtotal when cartItems state changes
  useEffect(() => {
    const newCartCount = cartItems.reduce((total, cartItem) => total + cartItem.quantity, 0);
    const newOriginalSubtotal = cartItems.reduce((total, cartItem) => total + cartItem.quantity * cartItem.price, 0);
    setCartCount(newCartCount);
    setOriginalSubtotal(newOriginalSubtotal);
  }, [cartItems]);

  // Update discount and cart total when cartItems or discount state changes
  useEffect(() => {
    let discountValue = 0;
    if (promoCode && stripeCouponObj) {
      if (stripeCouponObj.amount_off) {
        discountValue = stripeCouponObj.amount_off;
      } else if (stripeCouponObj.percent_off) {
        discountValue = (stripeCouponObj.percent_off / 100) * originalSubtotal;
      }
    }
    setDiscount(discountValue);
    const newTotal = originalSubtotal - discountValue;
    setCartTotal(newTotal);
  }, [cartItems, promoCode, stripeCouponObj, originalSubtotal]);

  // Auto-apply EHR-BETA coupon for EHR products
  const autoApplyEHRCoupon = () => {
    // Only apply if no coupon is currently active and coupons are loaded
    if (!promoCode && coupons.length > 0) {
      const ehrBetaCoupon = coupons.find((coupon) => coupon.name && coupon.name.toLowerCase() === "ehr-beta");

      if (ehrBetaCoupon) {
        setStripeCouponObj(ehrBetaCoupon);
        setPromoCode("EHR-BETA");
        setCouponNotValid(false);
      }
    }
  };

  const addItemToCart = (productToAdd) => {
    const updatedCartItems = addCartItem(cartItems, productToAdd);
    if (updatedCartItems) {
      setCartItems(updatedCartItems);

      // Auto-apply EHR-BETA coupon for EHR products
      if (productToAdd.metadata?.type === "ehr-subscription") {
        autoApplyEHRCoupon();
      }
    }
  };

  const removeItemFromCart = (cartItemToRemove) => {
    const updatedCartItems = removeCartItem(cartItems, cartItemToRemove);
    setCartItems(updatedCartItems);
  };

  const removeMultipleItemsFromCart = (itemsToRemove) => {
    const updatedCartItems = removeMultipleCartItems(cartItems, itemsToRemove);
    setCartItems(updatedCartItems);
  };

  const clearCart = () => {
    setCartItems([]);
    setPromoCode(null);
    setDiscount(0);
    setStripeCouponObj(null);
  };

  useEffect(() => {
    setIsCartEmpty(cartItems.length === 0);
  }, [cartItems]);

  const applyPromoCode = (code, coupons, cartItems) => {
    const coupon = validatePromoCode(code, coupons, cartItems);
    if (coupon) {
      setStripeCouponObj(coupon);
      setPromoCode(code);
      setCouponNotValid(false);
    } else {
      setStripeCouponObj(null);
      setPromoCode(null);
      setDiscount(0);
      setCouponNotValid(true);
    }
  };

  const removePromoCode = () => {
    setPromoCode(null);
    setDiscount(0);
  };

  // Reset couponNotValid when navigating away from the shop page or closing the cart
  useEffect(() => {
    return () => {
      setCouponNotValid(false);
    };
  }, [location, isCartOpen]);

  const getCartProductType = (cartItems) => {
    const hasEHRProducts = cartItems.some((item) => item.metadata?.type === "ehr-subscription");
    const hasKetamineProducts = cartItems.some(
      (item) => item.metadata?.type === "package" || (item.type === "package" && item.metadata?.type !== "ehr-subscription")
    );

    // Prioritize EHR if any EHR products exist
    if (hasEHRProducts) return "ehr";
    if (hasKetamineProducts) return "ketamine";
    return "ketamine"; // fallback
  };

  const value = {
    addItemToCart,
    cartItems,
    cartCount,
    removeItemFromCart,
    cartTotal,
    originalSubtotal,
    applyPromoCode,
    promoCode,
    removePromoCode,
    couponNotValid,
    discount,
    stripeCouponObj,
    isCartOpen,
    setIsCartOpen,
    setCouponNotValid,
    isCartEmpty,
    setIsCartEmpty,
    clearCart,
    getCartProductType,
    removeMultipleItemsFromCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
